import React from 'react'
import { PageBanner } from '../Banners/PageBanner'
import { useProductForm } from '../../hooks/useProductForm'
import ProductCategorySelector from './components/ProductCategorySelector'
import ProductFormatsSelector from './components/ProductFormatsSelector'
import FileUploader from './components/FileUploader'
import Loader from '../Common/Loader'
import ProductFilesUploader from './components/ProductFilesUploader'

const ProductForm: React.FC = () => {
  const {
    formData,
    loading,
    brands,
    selectedFile,
    previewUrl,
    mainCategories,
    subCategories,
    thirdLevelCategories,
    selectedMainCategory,
    selectedSubCategory,
    selectedThirdCategory,
    fileFormatOptions,
    handleChange,
    handleFileFormatChange,
    handleMainCategoryChange,
    handleSubCategoryChange,
    handleThirdCategoryChange,
    handleFileChange,
    handleSubmit,
    isEditMode,
    navigate,
    files,
    filesUploading,
    handleFilesUpload,
    handleRemoveFile
  } = useProductForm()

  if (loading) {
    return <Loader />
  }

  return (
    <>
      <PageBanner
        title={isEditMode ? 'Edytuj produkt' : 'Dodaj nowy produkt'}
        description={isEditMode ? 'Edytuj istniejący produkt' : 'Dodaj nowy produkt do marki'}
      />

      <section className='pt-50 pb-50'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-8 mx-auto'>
              <div className='card'>
                <div className='card-body'>
                  <form onSubmit={handleSubmit}>
                    {/* Podstawowe informacje */}
                    <div className='form-group'>
                      <label htmlFor='name'>Nazwa produktu</label>
                      <input
                        type='text'
                        className='form-control'
                        id='name'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        placeholder='Wprowadź nazwę produktu'
                        required
                      />
                    </div>

                    <div className='form-group'>
                      <label htmlFor='code'>Kod produktu</label>
                      <input
                        type='text'
                        className='form-control'
                        id='code'
                        name='code'
                        value={formData.code}
                        onChange={handleChange}
                        placeholder='Wprowadź kod produktu'
                      />
                    </div>

                    <div className='form-group mb-3'>
                      <label htmlFor='description'>Opis produktu</label>
                      <textarea
                        className='form-control'
                        id='description'
                        name='description'
                        rows={4}
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </div>

                    <div className='form-group mb-3'>
                      <label htmlFor='priority'>Priorytet</label>
                      <input
                        type='number'
                        className='form-control'
                        id='priority'
                        name='priority'
                        min='0'
                        max='10'
                        value={formData.priority}
                        onChange={handleChange}
                      />
                      <small className='form-text text-muted'>
                        Wyższy priorytet (1-10) oznacza wyższą pozycję na liście produktów. 0 oznacza brak priorytetu.
                      </small>
                    </div>

                    <div className='form-group mb-3'>
                      <label htmlFor='url'>URL</label>
                      <input
                        type='text'
                        className='form-control'
                        id='url'
                        name='url'
                        value={formData.url}
                        onChange={handleChange}
                      />
                    </div>

                    <div className='form-group mb-3'>
                      <label htmlFor='brandId'>Marka*</label>
                      <select
                        className='form-control'
                        id='brandId'
                        name='brandId'
                        value={formData.brandId}
                        onChange={handleChange}
                        required
                      >
                        <option value=''>Wybierz markę</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Kategorie produktu */}
                    <div className='mb-4'>
                      <h5 className='mb-3'>Kategoria produktu</h5>
                      <ProductCategorySelector
                        mainCategories={mainCategories}
                        subCategories={subCategories}
                        thirdLevelCategories={thirdLevelCategories}
                        selectedMainCategory={selectedMainCategory}
                        selectedSubCategory={selectedSubCategory}
                        selectedThirdCategory={selectedThirdCategory}
                        onMainCategoryChange={handleMainCategoryChange}
                        onSubCategoryChange={handleSubCategoryChange}
                        onThirdCategoryChange={handleThirdCategoryChange}
                      />
                    </div>

                    {/* Formaty plików */}
                    <div className='mb-4'>
                      <h5 className='mb-3'>Formaty plików</h5>
                      <ProductFormatsSelector
                        fileFormatOptions={fileFormatOptions}
                        selectedFormats={formData.fileFormats}
                        onChange={handleFileFormatChange}
                      />
                    </div>

                    {/* Obrazek produktu */}
                    <div className='mb-4'>
                      <h5 className='mb-3'>Obrazek produktu</h5>
                      <FileUploader
                        selectedFile={selectedFile}
                        previewUrl={previewUrl}
                        onChange={handleFileChange}
                        currentImageUrl={(formData as any).imagePresignedUrl || formData.image}
                      />
                    </div>

                    <h5 className='mb-3'>Pliki do pobrania</h5>
                    <ProductFilesUploader
                      files={files}
                      filesUploading={filesUploading}
                      onChange={handleFilesUpload}
                      onRemove={handleRemoveFile}
                    />

                    <div className='form-group d-flex justify-content-between mt-4'>
                      <button
                        type='button'
                        className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                        onClick={() => navigate(formData.brandId ? `/brands/${formData.brandId}` : '/brands')}
                      >
                        Anuluj
                      </button>

                      <button
                        type='submit'
                        className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                        disabled={loading}
                      >
                        {loading
                          ? (
                            <>
                              <span
                                className='tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent tw-mr-2'
                                role='status'
                                aria-hidden='true'
                              />
                              Zapisywanie...
                            </>
                            )
                          : (
                              isEditMode ? 'Zapisz zmiany' : 'Dodaj produkt'
                            )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ProductForm
